/**
 * An outreach thread's STAGE, and everything a surface draws from it — the dot, the row line, the stage
 * card, the speaker labels and the delivery line.
 *
 * The stage itself is computed by symphony-bff (`OutreachThreadRow.stage`, VMR #32078). The server sends
 * an ENUM plus facts (`needsYou`, `heldSend.sendAt`, `outcome`, `lastAuthor`, `lastDelivery`,
 * `closedByOwner`, `lastMessageAt`, `closedAt`), never a sentence; this module maps those onto catalog
 * keys. It returns SHAPES, not strings — a line is `{kind:'text', text}` (verbatim: a question, an
 * outcome) or `{kind:'key', key, params, suffix?}` — and rendering is a thin per-component step
 * (`components/symphony/contacts/StageLine.tsx`).
 *
 * ⚠️ A cross-surface CONTRACT. The same function names live in iOS `Store/OutreachStage.swift` and Android
 * `OutreachStage.kt`, and all three prove themselves against ONE fixture,
 * `native-apps/shared/outreach-stage/contract.json` (`lib/outreach-stage.test.ts` here). Change a rule and
 * you change the fixture and all three.
 *
 * No runtime imports, deliberately: this is a `lib/` module a jest suite loads directly, and anything that
 * reaches common-lib breaks jest in CI (see orion-web CLAUDE.md "Tests (jest)").
 */

// ── Vocabulary ─────────────────────────────────────────────────────────────────────────────────

/** The five stages, in PRECEDENCE order (lower rank = more pressing). */
export type OutreachStage = 'needsYou' | 'scheduled' | 'stuck' | 'handling' | 'done';
export const OUTREACH_STAGES: readonly OutreachStage[] = ['needsYou', 'scheduled', 'stuck', 'handling', 'done'];

export type OutreachAuthor = 'agent' | 'contact' | 'contactAgent' | 'ownerNote';
export type OutreachDelivery = 'sent' | 'delivered' | 'read' | 'failed' | 'unconfirmed';

/** The three groups the lists draw. */
export type StageGroupId = 'needsYou' | 'inProgress' | 'done';

/** A stage's dot — a Badge Style name (`statusInk` resolves it), or null for none. */
export type StageDot = 'waiting-on-you' | 'in-progress' | 'failed' | 'waiting-on-a-reply';

export interface StageGroup {
  id: StageGroupId;
  labelKey: string;
  stages: OutreachStage[];
  /** The `inbox_filter_status` BI value. */
  bi: string;
}

/** The groups, in render order. "Needs you" is hidden when empty; the status filter always offers all three. */
export const STAGE_GROUPS: readonly StageGroup[] = [
  { id: 'needsYou', labelKey: 'contacts.detail.needsYou', stages: ['needsYou'], bi: 'needs_you' },
  { id: 'inProgress', labelKey: 'tasks.status.inProgress', stages: ['scheduled', 'stuck', 'handling'], bi: 'in_progress' },
  { id: 'done', labelKey: 'tasks.status.done', stages: ['done'], bi: 'done' },
];

/** A param a line carries. TIME params are formatted by the surface with its own locale rules. */
export type StageParam =
  | { kind: 'text'; value: string }
  | { kind: 'key'; key: string }
  | { kind: 'day' | 'ago' | 'countdown'; at: string | null };

/** A line: verbatim text, or a catalog key with params (and an optional delivery-key suffix, " · "). */
export type StageLine =
  | { kind: 'text'; text: string }
  | { kind: 'key'; key: string; params: Record<string, StageParam>; suffix?: string };

/** A key-only label (speakers, the thread header). */
export type StageLabel = { key: string; params: Record<string, StageParam> };

export type StageCard =
  /** The shared prompt block, headed "{agent} is asking you" — prefixed by the channel in the Inbox. */
  | { kind: 'question'; header: { kind: 'key'; key: string; params: Record<string, StageParam> }; headerChannel: StageParam; decisionId: string }
  /** The row line, pinned (scheduled / stuck). */
  | { kind: 'line' }
  | { kind: 'outcome'; text: string }
  | { kind: 'closed'; key: string };

// ── The wire shapes this module reads ──────────────────────────────────────────────────────────

export interface StageNeedsYou {
  decisionId?: string;
  question?: string;
  kind?: string;
  choices?: { id?: string; label?: string }[];
  dueBy?: string;
  /** int64 — decimal text (web normalises it in `/api/contact-book`), tolerated as a flattened Long. */
  planVersion?: unknown;
  detail?: string;
  taskId?: string;
  conversationId?: string;
}

export interface StageMessage {
  text?: string;
  fromMe?: boolean;
  author?: string | null;
  /** The `Message from X:` / `Reply from X:` envelope's name — the legacy speaker fallback (`lib/outreach-thread-view.ts`). */
  senderName?: string;
  delivery?: string | null;
  /** `OutreachThreadMessage.answered_by_agent` (BFF field 8): the writing side's agent wrote it on its own. */
  answeredByAgent?: boolean;
}

export interface StageThread {
  outreachId?: string;
  channel?: string;
  status?: string;
  waitingOnMe?: boolean;
  stage?: string | null;
  needsYou?: StageNeedsYou | null;
  heldSend?: { heldSendId?: string; sendAt?: string } | null;
  outcome?: string;
  closedByOwner?: boolean;
  lastAuthor?: string | null;
  lastDelivery?: string | null;
  lastMessageAt?: string;
  updatedAt?: string;
  createdAt?: string;
  closedAt?: string;
  messages?: StageMessage[];
}

export interface StageEntry<T extends StageThread = StageThread> {
  stage?: string | null;
  outbound?: T[];
  inbound?: T[];
}

export type ThreadDirection = 'outbound' | 'inbound';

/** The prompt the shared block renders — structurally common-lib's `TaskDecisionPrompt`. */
export interface StagePrompt {
  decisionId: string;
  kind: string;
  question?: string;
  detail?: string;
  options?: { id: string; label: string }[];
  planVersion?: string;
  dueBy?: string;
  /** The thread the question is about, so an answer re-reads that contact's book (review rule 13). */
  outreachId?: string;
}

// ── Parsing ────────────────────────────────────────────────────────────────────────────────────

function enumWord(raw: string | null | undefined, prefix: string): string | null {
  const value = (raw ?? '').trim().toUpperCase();
  if (!value) return null;
  const bare = value.startsWith(prefix) ? value.slice(prefix.length) : value;
  return bare && bare !== 'UNKNOWN' ? bare : null;
}

const STAGE_WORDS: Record<string, OutreachStage> = {
  NEEDS_YOU: 'needsYou',
  SCHEDULED: 'scheduled',
  STUCK: 'stuck',
  HANDLING: 'handling',
  DONE: 'done',
};
const AUTHOR_WORDS: Record<string, OutreachAuthor> = {
  AGENT: 'agent',
  CONTACT: 'contact',
  CONTACT_AGENT: 'contactAgent',
  OWNER_NOTE: 'ownerNote',
};
const DELIVERY_WORDS: Record<string, OutreachDelivery> = {
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed',
  UNCONFIRMED: 'unconfirmed',
};

/** `OUTREACH_STAGE_*` (prefix optional, any case) → a stage; UNKNOWN / empty / unrecognised → null. */
export function parseStage(raw: string | null | undefined): OutreachStage | null {
  const word = enumWord(raw, 'OUTREACH_STAGE_');
  return (word && STAGE_WORDS[word]) || null;
}

export function parseAuthor(raw: string | null | undefined): OutreachAuthor | null {
  const word = enumWord(raw, 'OUTREACH_MESSAGE_AUTHOR_');
  return (word && AUTHOR_WORDS[word]) || null;
}

export function parseDelivery(raw: string | null | undefined): OutreachDelivery | null {
  const word = enumWord(raw, 'OUTREACH_DELIVERY_');
  return (word && DELIVERY_WORDS[word]) || null;
}

// ── Stage, rank, group, dot ────────────────────────────────────────────────────────────────────

/**
 * A thread's stage: the server's when it names one. Absent / UNKNOWN / unrecognised (Q4): status
 * `completed` (any case) → done, else handling. **Never needsYou without the server saying so** — a
 * `needsYou` payload alone does not raise it.
 */
export function threadStage(thread: StageThread): OutreachStage {
  const parsed = parseStage(thread.stage);
  if (parsed) return parsed;
  return (thread.status ?? '').trim().toLowerCase() === 'completed' ? 'done' : 'handling';
}

export function stageRank(stage: OutreachStage): number {
  return OUTREACH_STAGES.indexOf(stage);
}

export function stageGroup(stage: OutreachStage): StageGroupId {
  return STAGE_GROUPS.find((group) => group.stages.includes(stage))?.id ?? 'inProgress';
}

export function stageGroupLabelKey(group: StageGroupId): string {
  return STAGE_GROUPS.find((g) => g.id === group)?.labelKey ?? 'tasks.status.inProgress';
}

const DOTS: Record<OutreachStage, StageDot | null> = {
  needsYou: 'waiting-on-you',
  scheduled: 'in-progress',
  stuck: 'failed',
  handling: 'waiting-on-a-reply',
  done: null,
};

export function stageDot(stage: OutreachStage | null | undefined): StageDot | null {
  return stage ? DOTS[stage] : null;
}

const BI_VALUES: Record<OutreachStage, string> = {
  needsYou: 'needs_you',
  scheduled: 'scheduled',
  stuck: 'stuck',
  handling: 'handling',
  done: 'done',
};

/** The `additionalFields.stage` value on `open_inbox_row` / `open_contact` / `outreach_close`. */
export function stageBiValue(stage: OutreachStage): string {
  return BI_VALUES[stage];
}

// ── Params ─────────────────────────────────────────────────────────────────────────────────────

/** The channels with a `contacts.channel.*` key. Kept in step with `CHANNEL_SLOTS` in `contact-rows.ts`. */
const KNOWN_CHANNELS = ['whatsapp', 'email', 'app', 'sms', 'gmail_connector'];

/**
 * `{channel}` — the `contacts.channel.<lowercased>` key when one exists, else the raw value. An empty
 * channel still reads as `app` HERE, pinned by `native-apps/shared/outreach-stage/contract.json`. This is
 * no longer the chip's rule — `threadChannel` draws no chip for an empty (UNKNOWN) channel — and is left
 * as it is only because changing a stage line's wording is a contract change of its own.
 */
export function channelParam(channel: string | undefined): StageParam {
  const raw = (channel ?? '').trim();
  const value = raw.toLowerCase() || 'app';
  return KNOWN_CHANNELS.includes(value) ? { kind: 'key', key: `contacts.channel.${value}` } : { kind: 'text', value: raw };
}

/** The thread's time: `lastMessageAt ?? updatedAt ?? createdAt`. */
export function threadTime(thread: StageThread): string | undefined {
  return thread.lastMessageAt || thread.updatedAt || thread.createdAt || undefined;
}

function timeMs(at: string | undefined): number {
  if (!at) return 0;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? ms : 0;
}

/** The time a list sorts by: Done by `closedAt ?? time`, every other stage by its time. */
export function stageSortTime(thread: StageThread): number {
  if (threadStage(thread) === 'done' && thread.closedAt) return timeMs(thread.closedAt) || timeMs(threadTime(thread));
  return timeMs(threadTime(thread));
}

const text = (value: string): StageParam => ({ kind: 'text', value });
/** A time param; no timestamp at all is `at: null` (the contract's spelling), never `''`. */
const at = (kind: 'day' | 'ago' | 'countdown', value: string | undefined): StageParam => ({ kind, at: value || null });
const keyLine = (key: string, params: Record<string, StageParam> = {}, suffix?: string): StageLine =>
  suffix ? { kind: 'key', key, params, suffix } : { kind: 'key', key, params };

// ── The row line ───────────────────────────────────────────────────────────────────────────────

const HOUR_MS = 60 * 60 * 1000;

/** Who wrote last — the server's `lastAuthor`, else (an older read) `waitingOnMe` stands in for it. */
function lastAuthorOf(thread: StageThread): OutreachAuthor | null {
  const parsed = parseAuthor(thread.lastAuthor);
  if (parsed) return parsed;
  if (thread.waitingOnMe) return 'contact';
  return null;
}

/** The delivery suffix a handling wait line wears — SENT / DELIVERED / READ only. */
function waitSuffix(delivery: OutreachDelivery | null): string | undefined {
  return delivery === 'sent' || delivery === 'delivered' || delivery === 'read' ? deliveryLabelKey(delivery) ?? undefined : undefined;
}

/**
 * The line a row shows under the thread. `contactName` is the contact's display name (the existing
 * `contactDisplayName` fallback chain); `now` decides the scheduled countdown/send-at split.
 */
export function stageLine(thread: StageThread, contactName: string, now: Date = new Date()): StageLine {
  const stage = threadStage(thread);
  const name = text(contactName);
  const time = threadTime(thread);
  switch (stage) {
    case 'needsYou': {
      const question = (thread.needsYou?.question ?? '').trim();
      return question ? { kind: 'text', text: question } : keyLine('outreach.line.needsYouFallback');
    }
    case 'scheduled': {
      const sendAt = thread.heldSend?.sendAt;
      const sendMs = sendAt ? Date.parse(sendAt) : NaN;
      if (!sendAt || !Number.isFinite(sendMs)) return keyLine('contacts.viewer.sending');
      const ahead = sendMs - now.getTime();
      if (ahead > HOUR_MS) return keyLine('outreach.line.sendsAt', { time: at('day', sendAt) });
      if (ahead > 0) return keyLine('outreach.line.sendsIn', { time: at('countdown', sendAt) });
      return keyLine('contacts.viewer.sending');
    }
    case 'stuck':
      return parseDelivery(thread.lastDelivery) === 'failed'
        ? keyLine('outreach.line.notDelivered', { channel: channelParam(thread.channel) })
        : keyLine('outreach.line.noReplySince', { name, day: at('day', time) });
    case 'handling': {
      const author = lastAuthorOf(thread);
      if (author === 'contact' || author === 'contactAgent') return keyLine('outreach.line.replied', { name, ago: at('ago', time) });
      if ((thread.channel ?? '').trim().toLowerCase() === 'gmail_connector') return keyLine('outreach.line.gmail');
      return keyLine('outreach.line.waitingOn', { name, day: at('day', time) }, waitSuffix(parseDelivery(thread.lastDelivery)));
    }
    case 'done': {
      const outcome = (thread.outcome ?? '').trim();
      if (outcome) return { kind: 'text', text: outcome };
      return keyLine(thread.closedByOwner ? 'outreach.line.closedByYou' : 'outreach.line.closed');
    }
  }
}

/**
 * Whether a scheduled row's countdown ends on screen — i.e. it should refetch ONCE at zero. True for a
 * held send within the hour or already passed; false over an hour away or with no send time.
 */
export function refetchAtZero(thread: StageThread, now: Date = new Date()): boolean {
  if (threadStage(thread) !== 'scheduled') return false;
  const sendAt = thread.heldSend?.sendAt;
  const sendMs = sendAt ? Date.parse(sendAt) : NaN;
  if (!Number.isFinite(sendMs)) return false;
  return sendMs - now.getTime() <= HOUR_MS;
}

/** `m:ss` until `sendAt`, rounded UP to whole seconds (never `0:00` while still in the future), clamped at zero. */
export function countdown(sendAt: string | undefined, now: Date = new Date()): string {
  const sendMs = sendAt ? Date.parse(sendAt) : NaN;
  const seconds = Number.isFinite(sendMs) ? Math.max(0, Math.ceil((sendMs - now.getTime()) / 1000)) : 0;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest < 10 ? '0' : ''}${rest}`;
}

// ── The stage card ─────────────────────────────────────────────────────────────────────────────

/** The card pinned above a thread: the question block, the pinned line, the outcome, or none. */
export function stageCard(thread: StageThread): StageCard | null {
  const stage = threadStage(thread);
  switch (stage) {
    case 'needsYou': {
      const decisionId = thread.needsYou?.decisionId;
      return decisionId
        ? {
            kind: 'question',
            header: { kind: 'key', key: 'outreach.line.askingYou', params: {} },
            headerChannel: channelParam(thread.channel),
            decisionId,
          }
        : null;
    }
    case 'scheduled':
    case 'stuck':
      return { kind: 'line' };
    case 'handling':
      return null;
    case 'done': {
      const outcome = (thread.outcome ?? '').trim();
      if (outcome) return { kind: 'outcome', text: outcome };
      return { kind: 'closed', key: thread.closedByOwner ? 'outreach.line.closedByYou' : 'outreach.line.closed' };
    }
  }
}

function int64Text(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return /^[-+]?\d+$/.test(trimmed) ? trimmed.replace(/^\+/, '') : undefined;
  }
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : undefined;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'object') {
    const v = value as { low?: unknown; high?: unknown; unsigned?: unknown };
    if (Number.isInteger(v.low) && Number.isInteger(v.high)) {
      const combined = (BigInt((v.high as number) >>> 0) << BigInt(32)) | BigInt((v.low as number) >>> 0);
      return (v.unsigned === true ? BigInt.asUintN(64, combined) : BigInt.asIntN(64, combined)).toString();
    }
    const asText = (value as { toString?: () => string }).toString?.();
    return asText && /^[-+]?\d+$/.test(asText) ? asText : undefined;
  }
  return undefined;
}

/**
 * The thread's open question as the shared prompt block's `TaskDecisionPrompt`. Null without a
 * `decisionId`. `kind` defaults to `UNKNOWN_KIND`; a choice missing a half is dropped and `options` is
 * absent when none survive; `planVersion` is int64 text with "0" meaning absent (the verdict must echo an
 * unrevised plan back absent).
 */
export function needsYouPrompt(thread: StageThread): StagePrompt | null {
  const needsYou = thread.needsYou;
  const decisionId = needsYou?.decisionId?.trim();
  if (!needsYou || !decisionId) return null;
  const prompt: StagePrompt = { decisionId, kind: needsYou.kind?.trim() || 'UNKNOWN_KIND' };
  const question = needsYou.question?.trim();
  if (question) prompt.question = question;
  const detail = needsYou.detail?.trim();
  if (detail) prompt.detail = detail;
  const options = (needsYou.choices ?? [])
    .filter((choice) => !!choice?.id && !!choice?.label)
    .map((choice) => ({ id: choice.id as string, label: choice.label as string }));
  if (options.length > 0) prompt.options = options;
  const planVersion = int64Text(needsYou.planVersion);
  if (planVersion && planVersion !== '0') prompt.planVersion = planVersion;
  if (needsYou.dueBy) prompt.dueBy = needsYou.dueBy;
  const outreachId = thread.outreachId?.trim();
  if (outreachId) prompt.outreachId = outreachId;
  return prompt;
}

// ── Speakers and delivery ──────────────────────────────────────────────────────────────────────

/** Who a message is FROM once the fallbacks are applied: OWNER_NOTE reads as the agent until phase 4;
 *  an unknown author falls back on `fromMe`. */
export function resolvedSpeaker(message: StageMessage): 'agent' | 'contact' | 'contactAgent' {
  const author = parseAuthor(message.author);
  if (author === 'agent' || author === 'ownerNote') return 'agent';
  if (author === 'contact' || author === 'contactAgent') return author;
  return message.fromMe ? 'agent' : 'contact';
}

/**
 * Whether message `index` opens a new speaker run — i.e. carries the speaker label. A run breaks whenever
 * the RESOLVED speaker changes (rule 10), so the contact handing over to their own agent starts a new run
 * even though both sit on the same side.
 */
export function startsSpeakerRun(messages: StageMessage[] | undefined, index: number): boolean {
  const list = messages ?? [];
  if (index <= 0) return true;
  return resolvedSpeaker(list[index]) !== resolvedSpeaker(list[index - 1]);
}

export interface SpeakerContext {
  contactName: string;
  /** The contact's OWN assistant's name (`entry.agentName`), when they run one. */
  agentName?: string;
  channel?: string;
}

/**
 * Who a message is from, as a label. A message the OTHER side's assistant wrote on its own
 * (`answeredByAgent`, outreach visibility phase 4 — `native-apps/shared/outreach-composer/contract.json`
 * `speakerCases`) reads "{name}'s assistant" whichever of CONTACT / CONTACT_AGENT it resolved to; on the
 * owner's own side the flag is ignored.
 */
export function speakerLabel(message: StageMessage, ctx: SpeakerContext): StageLabel {
  const speaker = resolvedSpeaker(message);
  if (speaker === 'agent') return { key: 'outreach.speaker.agent', params: {} };
  if (message.answeredByAgent === true) return { key: 'outreach.speaker.assistantAnswered', params: { name: text(ctx.contactName) } };
  if (speaker === 'contactAgent') {
    const agent = (ctx.agentName ?? '').trim();
    return agent
      ? { key: 'outreach.speaker.contactAgent', params: { name: text(ctx.contactName), agent: text(agent) } }
      : { key: 'outreach.speaker.contactAgentUnnamed', params: { name: text(ctx.contactName) } };
  }
  return { key: 'outreach.personOnChannel', params: { name: text(ctx.contactName), channel: channelParam(ctx.channel) } };
}

/** The `outreach.delivery.*` key; UNCONFIRMED / missing → null (render nothing). */
export function deliveryLabelKey(delivery: OutreachDelivery | null | undefined): string | null {
  return delivery && delivery !== 'unconfirmed' ? `outreach.delivery.${delivery}` : null;
}

/** The index of the last message whose RESOLVED speaker is our agent, or null when there is none. */
export function lastAgentMessageIndex(messages: StageMessage[] | undefined): number | null {
  const list = messages ?? [];
  for (let i = list.length - 1; i >= 0; i -= 1) if (resolvedSpeaker(list[i]) === 'agent') return i;
  return null;
}

/** The delivery line a thread renders — under the last agent message only — or null. */
export function lastAgentDelivery(messages: StageMessage[] | undefined): { index: number; key: string } | null {
  const index = lastAgentMessageIndex(messages);
  if (index === null) return null;
  const key = deliveryLabelKey(parseDelivery((messages ?? [])[index]?.delivery));
  return key ? { index, key } : null;
}

// ── Contacts ───────────────────────────────────────────────────────────────────────────────────

export type UniqueThread<T> = { thread: T; direction: ThreadDirection };

/**
 * A contact's threads once each (Q8): outbound, then inbound whose `outreachId` was not seen — a
 * self-outreach (its own source AND target) arrives in BOTH lists, and its outbound copy wins. Every
 * surface that lists or counts a contact's threads goes through this; a raw outbound+inbound concat
 * double-counts.
 */
export function uniqueThreads<T extends StageThread>(entry: StageEntry<T>): UniqueThread<T>[] {
  const seen = new Set<string>();
  const out: UniqueThread<T>[] = [];
  const add = (thread: T, direction: ThreadDirection) => {
    const id = thread.outreachId ?? '';
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    }
    out.push({ thread, direction });
  };
  for (const thread of entry.outbound ?? []) add(thread, 'outbound');
  for (const thread of entry.inbound ?? []) add(thread, 'inbound');
  return out;
}

/** A contact's stage: the server's `entry.stage` when parseable, else the worst of its threads; null with none. */
export function contactStage(entry: StageEntry): OutreachStage | null {
  const parsed = parseStage(entry.stage);
  if (parsed) return parsed;
  let worst: OutreachStage | null = null;
  for (const { thread } of uniqueThreads(entry)) {
    const stage = threadStage(thread);
    if (worst === null || stageRank(stage) < stageRank(worst)) worst = stage;
  }
  return worst;
}

/** The thread a contact row quotes: lowest rank, then newest time. */
export function mostPressingThread<T extends StageThread>(entry: StageEntry<T>): UniqueThread<T> | null {
  let best: UniqueThread<T> | null = null;
  for (const unique of uniqueThreads(entry)) {
    if (!best) {
      best = unique;
      continue;
    }
    const rank = stageRank(threadStage(unique.thread));
    const bestRank = stageRank(threadStage(best.thread));
    if (rank < bestRank || (rank === bestRank && timeMs(threadTime(unique.thread)) > timeMs(threadTime(best.thread)))) best = unique;
  }
  return best;
}

/**
 * The join a bridge-fed surface (task "Messages sent", the thread modal, home feed rows) makes against the
 * contact-book head. The outbound copy wins; a miss is null, and the caller renders today's fallback.
 */
export function threadByOutreachId<T extends StageThread, E extends StageEntry<T>>(
  entries: E[] | undefined,
  outreachId: string | undefined,
): { entry: E; thread: T; direction: ThreadDirection } | null {
  if (!outreachId) return null;
  for (const entry of entries ?? []) {
    const hit = uniqueThreads(entry).find(({ thread }) => thread.outreachId === outreachId);
    if (hit) return { entry, thread: hit.thread, direction: hit.direction };
  }
  return null;
}

/**
 * Items grouped by their thread's stage group, in `STAGE_GROUPS` order, each group by time desc (Done by
 * `closedAt ?? time`). Inside "In progress" stages are NOT re-ranked — recency only. Every group is
 * returned; the renderer hides an empty one.
 */
export function groupByStage<I>(items: I[], threadOf: (item: I) => StageThread): { group: StageGroup; items: I[] }[] {
  return STAGE_GROUPS.map((group) => ({
    group,
    items: items
      .filter((item) => group.stages.includes(threadStage(threadOf(item))))
      .map((item) => ({ item, time: stageSortTime(threadOf(item)) }))
      .sort((a, b) => b.time - a.time)
      .map(({ item }) => item),
  }));
}

/** Items by stage rank, then recency — the contact screen's ONE "Conversations" list (Q10). */
export function sortByRankThenRecency<I>(items: I[], threadOf: (item: I) => StageThread): I[] {
  return items
    .map((item) => ({ item, rank: stageRank(threadStage(threadOf(item))), time: stageSortTime(threadOf(item)) }))
    .sort((a, b) => a.rank - b.rank || b.time - a.time)
    .map(({ item }) => item);
}

/** A contact-screen row's line: `outreach.line.withGroup {group, line}`. */
export function withGroupLine(thread: StageThread, contactName: string, now: Date = new Date()): {
  key: 'outreach.line.withGroup';
  params: { group: { kind: 'key'; key: string }; line: StageLine };
} {
  return {
    key: 'outreach.line.withGroup',
    params: { group: { kind: 'key', key: stageGroupLabelKey(stageGroup(threadStage(thread))) }, line: stageLine(thread, contactName, now) },
  };
}

/**
 * The contact screen (Q10): the open-question cards on top (one per needsYou thread with a decision), then
 * ONE list titled `contacts.detail.conversations {count}` sorted by rank then recency.
 */
export function contactScreen<T extends StageThread>(
  entry: StageEntry<T>,
  contactName: string,
  now: Date = new Date(),
): {
  questionThreads: UniqueThread<T>[];
  title: { key: 'contacts.detail.conversations'; params: { count: number } };
  rows: (UniqueThread<T> & { line: ReturnType<typeof withGroupLine> })[];
} {
  const unique = uniqueThreads(entry);
  const sorted = sortByRankThenRecency(unique, (u) => u.thread);
  return {
    questionThreads: sorted.filter(({ thread }) => stageCard(thread)?.kind === 'question'),
    title: { key: 'contacts.detail.conversations', params: { count: unique.length } },
    rows: sorted.map((u) => ({ ...u, line: withGroupLine(u.thread, contactName, now) })),
  };
}

/** The thread header subtitle (Q10): started by you (outbound) / by them (inbound), on `createdAt`. */
export function threadHeader(thread: StageThread, direction: ThreadDirection, contactName: string): StageLabel {
  const channel = channelParam(thread.channel);
  const day = at('day', thread.createdAt);
  return direction === 'outbound'
    ? { key: 'outreach.thread.startedByYou', params: { channel, day } }
    : { key: 'outreach.thread.startedByThem', params: { channel, name: text(contactName), day } };
}
